import React, { FC, useCallback, useMemo, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Form from 'src/common/Form';
import Input from 'src/common/Input';
import notification from 'src/common/notification';
import styles from './index.module.scss';
import classNames from 'classnames';
import QrCodeLink from './QrCodeLink';
import AuthBack from 'src/pages/AuthPage/AuthBack';
import { Button } from 'src/common';

export interface IProps {
  withBackButton?: boolean;
  extra?: ReactNode;
  submitTitle: string;
  code: string;
  username: string;
  onAfterSubmit: () => void;
  verifyTotpToken: (totpToken: string) => Promise<void>;
  setPreferredMFA: () => Promise<void>;
}

interface IFormState {
  code: string;
}

export const SetupMfaQrCode: FC<IProps> = (props: IProps) => {
  const { code, withBackButton, username, extra, submitTitle, onAfterSubmit, verifyTotpToken, setPreferredMFA } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = useMemo((): IFormState => {
    return {
      code: ''
    };
  }, []);

  const setCodeError = useCallback(
    (error: string) => {
      form.setFields([
        {
          name: 'code',
          errors: [error]
        }
      ]);
    },
    [form]
  );
  const onFinish = useCallback(
    (values: IFormState) => {
      const { code } = values as IFormState;
      setIsLoading(true);
      verifyTotpToken(code)
        .then(() => {
          setPreferredMFA()
            .then(() => {
              onAfterSubmit();
            })
            .catch(() => {
              notification.error({
                description: t('Common.unknownErrorDesc'),
                message: t('Common.unknownError')
              });
            });
        })
        .catch(err => {
          const { code } = err;
          if (code) {
            if (code === 'InvalidParameterException') {
              setCodeError(t('Auth.codeLengthError'));
            } else if (code === 'NotAuthorizedException') {
              setCodeError(t('Auth.invalidTokenError'));
            } else {
              setCodeError(t('Common.unknownErrorDesc'));
            }
          } else {
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [onAfterSubmit, setCodeError, setPreferredMFA, t, verifyTotpToken]
  );
  const onValuesChange = useCallback(
    (changedValues: Partial<IFormState>) => {
      setIsDisabled(!changedValues.code);
    },
    [setIsDisabled]
  );

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        layout={'vertical'}
        className={classNames(styles.form, { [styles.authForm]: withBackButton })}
      >
        <div className={styles.setupStepsContent}>
          <ul className={styles.setupSteps}>
            <li className={styles.setupStep}>
              <span className={styles.stepTitle}>{`1. ${t('Security.setupMfaStep1Title')}`}</span>
              <div className={styles.stepContent}>
                <p>{t('Security.setupMfaStep1Desc')}</p>
              </div>
            </li>
            <li className={styles.setupStep}>
              <span className={styles.stepTitle}>{`2. ${t('Security.setupMfaStep2Title')}`}</span>
              <div className={styles.stepContent}>
                <p>{t('Security.setupMfaStep2Desc')}</p>
              </div>
            </li>
            {!!username && code && (
              <QrCodeLink
                username={username}
                code={code}
                header={`3. ${t('Security.setupMfaStep3Title')}`}
                description={t('Security.setupMfaStep3Desc')}
              />
            )}
            <li className={styles.setupStep}>
              <span className={styles.stepTitle}>{`4. ${t('Security.setupMfaStep4Title')}`}</span>
              <div className={styles.stepContent}>
                <p>{t('Security.setupMfaStep4Desc')}</p>
              </div>
            </li>
          </ul>
        </div>
        <Form.Item
          className={styles.formItem}
          name="code"
          label={t('Security.enterGeneratedCode')}
          rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Auth.code') }) }]}
        >
          <Input name="code" placeholder={t('Auth.code')} autoComplete={'one-time-code'} type={'number'} />
        </Form.Item>
        <Form.Item className={styles.authSubmit}>
          {extra}
          <Button htmlType="submit" type={'primary'} loading={isLoading} disabled={isDisabled}>
            {submitTitle}
          </Button>
        </Form.Item>
        {withBackButton && (
          <Form.Item className={styles.authBack}>
            <AuthBack />
          </Form.Item>
        )}
      </Form>
    </>
  );
};

export default SetupMfaQrCode;
